import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode.react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { AuthContext } from '../components/AuthProvider';
import {
	get_scan_tokens,
	update_scan_token
} from "../tools-network";

import { showTemporaryMessage } from "../components/Message";
import Scans from "../components/Scans";
import ScanTokenNameChange from "../components/ScanTokenNameChange";
import ScanTokenColorChange from "../components/ScanTokenColorChange";
import {
	generate_qr_code
} from '../tools';

const WRISTBAND_COLORS = [
    { value: 'white', hex: '#FFFFFF', border: true },
    { value: 'black', hex: '#000000' },
    { value: 'red', hex: '#DC2626' },
    { value: 'blue', hex: '#2563EB' },
    { value: 'green', hex: '#16A34A' },
    { value: 'yellow', hex: '#EAB308' },
    { value: 'pink', hex: '#EC4899' },
    { value: 'purple', hex: '#9333EA' },
    { value: 'orange', hex: '#EA580C' }
];

const getColorInfo = (colorValue) => {
    return WRISTBAND_COLORS.find(c => c.value === colorValue) || WRISTBAND_COLORS[0];
};

const ScanTokens = ({loginToken, bearerId, bearerName, isEditingBearer, reportWristbandEditing}) => {
	const { t } = useTranslation();
	const { userType, userId } = React.useContext(AuthContext);
	const isAdmin = userType === 'ADMIN' || userType === 'SUPERUSER';
	const [showDeregisterConfirm, setShowDeregisterConfirm] = useState(false);
	const [deregisterConfirmName, setDeregisterConfirmName] = useState('');
	const [isEditingWristbandName, setIsEditingWristbandName] = useState(false);
	const [isEditingWristbandColor, setIsEditingWristbandColor] = useState(false);
	const [showSimulateScanModal, setShowSimulateScanModal] = useState(false);

	// Array of scan tokens data received from the server
	const [scanTokens, setScanTokens] = useState([]);
	// Selected object part of the scanTokens array; contains name and other data in addition to the actual token
	const [selectedScanToken, setSelectedScanToken] = useState(null);

	const updateListOfScanTokens = async () => {
		try {
			const res = await get_scan_tokens(loginToken, bearerId);
			if(res.status) {
				const scanTokens = res.data ? res.data : [];
				setScanTokens(scanTokens);
				// Re-select the previously selected token if it still exists
				if (selectedScanToken) {
					const stillExists = scanTokens.find(token => token.token === selectedScanToken.token);
					setSelectedScanToken(stillExists || null);
				} else {
					setSelectedScanToken(null);
				}
			} else {
				showTemporaryMessage("Fetch scan tokens FAILED!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Fetch scan tokens FAILED!", err, 'danger');
		}
	};

	useEffect(() => {
		updateListOfScanTokens();
	}, [bearerId]);

	useEffect(() => {
		if (reportWristbandEditing) {
			reportWristbandEditing(isEditingWristbandName || isEditingWristbandColor);
		}
	}, [isEditingWristbandName, isEditingWristbandColor, reportWristbandEditing]);

	const handleScanTokenChange = (token) => {
		const selectedTokenObject = scanTokens.find(scanToken => scanToken.token === token);
		setSelectedScanToken(selectedTokenObject);
	};

	const confirmDeregisterWristband = async () => {
		if (deregisterConfirmName !== selectedScanToken.name) {
			showTemporaryMessage(t("name_does_not_match"), "", 'danger');
			return;
		}
		const updateFields = { bearer_id: null };
		try {
			const res = await update_scan_token(loginToken, selectedScanToken.token, updateFields);
			if(res.success) {
				showTemporaryMessage(t("wristband_successfully_deregistered"), " ", 'success');
				setShowDeregisterConfirm(false);
				setDeregisterConfirmName('');
				updateListOfScanTokens();
			} else {
				showTemporaryMessage("Failed to deregister wristband!", res.msg, 'danger');
			}
		} catch (err) {
			showTemporaryMessage("Failed to deregister wristband!", err, 'danger');
		}
	}

	return (
		<>
			<div className="w-full mx-auto py-4">
				<h2 className="text-center font-bold text-3xl mb-4">{t("scan_tokens")}</h2>
				<div className="flex flex-row gap-4">
					<div className="flex-1">
						<div className="basis-1/3">
							{/* Scan token details */}
							<Listbox
								as="div"
								value={selectedScanToken ? selectedScanToken.token : null}
								onChange={handleScanTokenChange}
								>
								{({ open }) => (
									<>
										<div className="relative mt-2">
								<Listbox.Button className="inline-flex w-full justify-between gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed" disabled={isEditingBearer || isEditingWristbandName || isEditingWristbandColor}>
												<div className="flex items-center gap-2">
													{selectedScanToken && (() => {
														const colorInfo = getColorInfo(selectedScanToken.color);
														return (
															<div
																className={`w-4 h-4 rounded-full flex-shrink-0 ${colorInfo.border ? 'border-2 border-gray-300' : ''}`}
																style={{ backgroundColor: colorInfo.hex }}
															/>
														);
													})()}
													<span>{scanTokens.length == 0 ? t("no_scan_tokens") : selectedScanToken ? (selectedScanToken.name && selectedScanToken.name.trim() ? selectedScanToken.name : selectedScanToken.token) : t("select_scan_token")}</span>
												</div>
												<ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
											</Listbox.Button>
											<Transition
												as={Fragment}
												enter="transition ease-out duration-100"
												enterFrom="transform opacity-0 scale-95"
												enterTo="transform opacity-100 scale-100"
												leave="transition ease-in duration-75"
												leaveFrom="transform opacity-100 scale-100"
												leaveTo="transform opacity-0 scale-95">
												<Listbox.Options
													className="absolute right-0 z-10 mt-1 w-full max-h-[80vh] overflow-auto origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
													style={{ display: open ? 'block' : 'none' }}
													>
													{/* We want to show the name if it exists instead of the actual token. The name should always exist, so this is just a fallback in case something goes wrong */}
													{scanTokens.map(({ token, name, color }) => {
														const colorInfo = getColorInfo(color);
														return (
															<Listbox.Option key={token} value={token}>
																{({ active }) => (
																	<div
																		className={`${
																			active ? 'bg-gray-100' : ''
																				} cursor-pointer select-none relative py-2 pl-3 pr-9 flex items-center gap-2`}>
																		<div
																			className={`w-4 h-4 rounded-full flex-shrink-0 ${colorInfo.border ? 'border-2 border-gray-300' : ''}`}
																			style={{ backgroundColor: colorInfo.hex }}
																		/>
																		<span>{(name && name.trim()) ? name : token}</span>
																	</div>
																)}
															</Listbox.Option>
														);
													})}
												</Listbox.Options>
											</Transition>
										</div>
									</>
								)}
							</Listbox>

						{/* Action buttons for scan token */}
					{selectedScanToken && !isEditingBearer && (
						<div className="mt-4 flex flex-row gap-3">
							{/* Change name of scan token */}
							<ScanTokenNameChange
								loginToken={loginToken}
								userId={userId}
								scanToken={selectedScanToken}
								updateListOfScanTokens={updateListOfScanTokens}
								reportEditingState={setIsEditingWristbandName}
								isOtherEditing={isEditingWristbandColor}
								/>

							{/* Change color of scan token */}
							<ScanTokenColorChange
								loginToken={loginToken}
								scanToken={selectedScanToken}
								updateListOfScanTokens={updateListOfScanTokens}
								reportEditingState={setIsEditingWristbandColor}
								isOtherEditing={isEditingWristbandName}
								/>

							{/* Deregister wristband */}
							{!isEditingWristbandName && !isEditingWristbandColor && (
								<button
									id="deregister-wristband-button"
									onClick={() => setShowDeregisterConfirm(true)}
								className="bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700 transition">
										{t("deregister_wristband")}
								</button>
							)}
					</div>
				)}


					{/* Display QR code for admin users */}
					{isAdmin && selectedScanToken &&
						<div className="mt-6 p-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl border-2 border-cyan-200 shadow-sm">
							<h3 className="text-lg font-bold text-cyan-900 mb-3">{t("wristband_qr_code")}</h3>
							<p className="text-sm text-gray-600 mb-4">
								{t("token")}: <span className="font-mono text-xs bg-white px-2 py-1 rounded border border-gray-200">{selectedScanToken.token}</span>
							</p>
							<div className="flex items-center gap-6">
								<div className="bg-white p-3 rounded-lg border-2 border-cyan-300 shadow-md">
									<QRCode value={generate_qr_code(selectedScanToken.token)} size={160} />
								</div>
								<button
									onClick={() => setShowSimulateScanModal(true)}
									className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-base font-semibold shadow-md hover:shadow-lg transition-all">
									{t("simulate_scan")}
								</button>
							</div>
						</div>
					}
					{/* BEARER HISTORY (admin only) */}
					{isAdmin && selectedScanToken && selectedScanToken.bearer_history && selectedScanToken.bearer_history.length > 0 && (
						<div className="mt-4 border-2 border-amber-200 rounded-lg p-4 bg-amber-50">
							<h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center">
								<svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								Armbandets historik
							</h4>
							<div className="space-y-2">
								{selectedScanToken.bearer_history.map((entry, idx) => (
									<div key={idx} className="bg-white rounded-md p-3 shadow-sm border border-gray-100 text-sm">
										<div className="flex justify-between items-start">
											<div>
												<span className="font-medium">{entry.bearer_name || `Bärare #${entry.bearer_id}`}</span>
												{entry.user_name && <span className="text-gray-500 ml-1">(ägare: {entry.user_name})</span>}
											</div>
										</div>
										<div className="text-xs text-gray-400 mt-1">
											{entry.linked_at && <span>Registrerad: {new Date(entry.linked_at).toLocaleString('sv-SE')}</span>}
											{entry.linked_at && entry.unlinked_at && <span className="mx-1">→</span>}
											{entry.unlinked_at && <span>Avregistrerad: {new Date(entry.unlinked_at).toLocaleString('sv-SE')}</span>}
										</div>
									</div>
								))}
							</div>
						</div>
					)}
											{(userType == 'ADMIN' || userType == 'SUPERUSER') &&
							<Scans
								loginToken={loginToken}
								scanToken={selectedScanToken?.token}
							/>
						}
			{/* SIMULATE SCAN MODAL */}
			{showSimulateScanModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
						<h3 className="text-lg font-bold text-gray-900 mb-4">{t("simulate_scan_options")}</h3>
						<p className="text-sm text-gray-600 mb-6">{t("choose_sms_option")}</p>
						<div className="space-y-3">
							<button
								onClick={() => {
									window.open(generate_qr_code(selectedScanToken.token), '_blank');
									setShowSimulateScanModal(false);
								}}
								className="w-full px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 text-left">
								<span className="font-semibold">{t("scan_with_sms_prefix")}</span> <span className="font-bold">{t("scan_with_sms_bold")}</span> <span className="font-semibold">{t("scan_with_sms_suffix")}</span>
							</button>
							<button
								onClick={() => {
									window.open(generate_qr_code(selectedScanToken.token) + '&no_sms=1', '_blank');
									setShowSimulateScanModal(false);
								}}
								className="w-full px-4 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 text-left">
								<span className="font-semibold">{t("scan_without_sms_prefix")}</span> <span className="font-bold">{t("scan_without_sms_bold")}</span> <span className="font-semibold">{t("scan_without_sms_suffix")}</span>
							</button>
						</div>
						<button
							onClick={() => setShowSimulateScanModal(false)}
							className="mt-4 w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
							{t("cancel")}
						</button>
					</div>
				</div>
			)}
				{/* DEREGISTER CONFIRMATION MODAL */}
				{showDeregisterConfirm && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
						<div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
							<h3 className="text-lg font-bold text-gray-900 mb-4">{t("confirm_deregister_wristband")}</h3>
							{/* Warning message */}
							<div className="bg-red-50 border-l-4 border-red-600 p-4 mb-4">
								<div className="flex">
									<div className="ml-3">
										<h3 className="text-sm font-bold text-red-800">
											{t("deregister_wristband_warning_title")}
										</h3>
										<div className="mt-2 text-sm text-red-700">
										<p>{t("deregister_wristband_warning_message", { name: bearerName })}</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
								<div className="flex">
									<div className="ml-3">
										<p className="text-sm text-blue-700">{t("deregister_wristband_info_message")}</p>
									</div>
								</div>
							</div>
							<p className="text-sm text-gray-700 mb-4">
								{t("type_name_to_confirm_deregister")}: <span className="font-bold">{selectedScanToken.name}</span>
							</p>
							<input
								type="text"
								value={deregisterConfirmName}
								onChange={(e) => setDeregisterConfirmName(e.target.value)}
								className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
								placeholder={selectedScanToken.name}
							/>
							<div className="flex gap-3 justify-end">
								<button
									onClick={() => {
										setShowDeregisterConfirm(false);
										setDeregisterConfirmName('');
									}}
									className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
										{t("cancel")}
								</button>
								<button
									onClick={confirmDeregisterWristband}
									disabled={deregisterConfirmName !== selectedScanToken.name}
									className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
										{t("deregister_wristband")}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	</div>
</div>
</>
);
};

export default ScanTokens;
